import { Formik, FormikConfig, FormikErrors, FormikValues, useFormikContext } from 'formik';
import { ReactNode, useEffect } from 'react';
import { ScrollToFieldError } from '@synergy/components';
import { noop } from '@synergy/utils';

import { PaloDirtyComparison, PaloDirtyReporter, PaloDirtyStepScope } from '../palo/palo-dirty-context';
import { useFormikGroupContext } from './formik-group-context';

export type SubmitData<T> = { values: T; isDirty: boolean; isValid: boolean; errors: FormikErrors<T> };
export type SubmitFn<T> = (submitData: SubmitData<T>) => void;

export interface FormikGroupItemProps<Values extends FormikValues = FormikValues>
    extends Omit<FormikConfig<Values>, 'onSubmit'> {
    groupItemId: number;
    isDirty?: boolean;
    onSubmit: SubmitFn<Values>;
    /**
     * The document as the server has it, in this step's value shape (#2137).
     *
     * Without it the unsaved-changes marker is measured against Formik's own
     * initial values, and a wizard defeats that: leaving a step parks its input
     * in the wizard's state and coming back hands the parked input in as the
     * initial values, so Formik reports the step as saved when nothing was.
     *
     * Pass this and the marker means what a reader expects — not yet on the
     * server — across any number of step changes.
     */
    dirtyBaseline?: unknown;
    /** Brings the values into the baseline's shape; see PaloDirtyComparison. */
    normalizeForDirty?: (values: Values) => unknown;
}

export const FormikGroupItem = <Values extends FormikValues = FormikValues>(
    props: FormikGroupItemProps<Values>
): ReactNode => {
    const { children, onSubmit, groupItemId, isDirty, dirtyBaseline, normalizeForDirty, ...formikProps } = props;

    const body = (
        <>
            <ScrollToFieldError />
            {undefined === dirtyBaseline ? (
                <PaloDirtyReporter />
            ) : (
                <PaloDirtyComparison baseline={dirtyBaseline} normalize={normalizeForDirty} />
            )}
            <FormikSubPageHelper itemId={groupItemId} onSubmit={onSubmit} isDirty={isDirty} />
            {children}
        </>
    );

    return (
        <Formik {...formikProps} onSubmit={noop}>
            {undefined === dirtyBaseline ? (
                body
            ) : (
                // Each step reports under its own key, so leaving one does not
                // wipe what another had to say. Without this every step of the
                // wizard shares a single key and the last unmount clears it —
                // which is why the marker used to vanish on a step change.
                <PaloDirtyStepScope index={groupItemId}>{body}</PaloDirtyStepScope>
            )}
        </Formik>
    );
};

export interface FormikSubPageProps<T> {
    itemId: number;
    isDirty?: boolean;
    onSubmit: SubmitFn<T>;
}

const FormikSubPageHelper = <T,>(props: FormikSubPageProps<T>): ReactNode => {
    const formikRef = useFormikContext<T>();
    const formikGroupContext = useFormikGroupContext();
    const { unregister } = formikGroupContext;
    const { itemId } = props;

    // Saving reaches every form on screen, so one that has left has to say so.
    useEffect(() => () => unregister(itemId), [unregister, itemId]);

    formikGroupContext.register(props.itemId, {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        validate: async () => formikRef.validateForm() as any,
        submit: async () => {
            try {
                // What the validation just found, not what the form held when
                // this closure was made. Reading `errors` off the captured
                // context after the await hands back the state of that earlier
                // render, so the first save saw a form without errors: nothing
                // was written, nothing was marked, and the mask still reported
                // a result (#2210).
                const errors = await formikRef.validateForm();
                const isValid = 0 === Object.keys(errors).length;

                // Runs anyway: this is what marks the fields as touched, and
                // an untouched field shows no error however invalid it is.
                await formikRef.submitForm();

                const isDirty = props.isDirty ?? formikRef.dirty;
                await props.onSubmit({ values: formikRef.values, isDirty, isValid, errors });
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                return errors as any;
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(
                    'Error while subpage formik submit.\nThis may occur if the submit call is not awaited correctly.'
                );
                throw e;
            }
        },
        reset: () => formikRef.resetForm(),
    });

    return null;
};
